import i18n from '@/i18n'
import type { components } from '@/shared/api/schema'
import { apiClient, apiRequest } from '@/shared/api/client'

export type DashboardOrder = components['schemas']['Order'] & {
  package_id?: string
  alias?: string
  proxy_password?: string
  data?: number
  data_spent?: number
  data_updated?: string
  proxy_password_reset?: string | null
  pools?: string[]
}

export type AuthUser = components['schemas']['User'] & {
  orders?: DashboardOrder[]
  invoices?: components['schemas']['Invoice'][]
  is_admin?: boolean
  is_2fa_enabled?: boolean
  is_newsletter_subscribed?: boolean
  is_notify_low_data?: boolean
  points?: number
  level?: string
}

export type AuthAttribution = {
  affiliateCode?: string
  referralCode?: string
}

function attributionBody(attribution?: AuthAttribution) {
  return {
    ...(attribution?.affiliateCode ? { affiliate_code: attribution.affiliateCode } : {}),
    ...(attribution?.referralCode ? { referral_code: attribution.referralCode } : {}),
  }
}

export async function loginWithPassword(email: string, password: string) {
  const result = await apiRequest(() => apiClient.POST('/login', { body: { email, password } }), i18n.global.t('api.login'))
  return requireTokens(result)
}

export async function getAuthenticatedUser(): Promise<AuthUser> {
  return apiRequest(() => apiClient.GET('/profile'), i18n.global.t('api.load_account'))
}

export async function loginWithGoogle(credential: string, attribution?: AuthAttribution) {
  const result = await apiRequest(
    () => apiClient.POST('/login/google', { body: { credential, ...attributionBody(attribution) } }),
    i18n.global.t('api.google'),
  )
  return requireTokens(result)
}

export function signUp(email: string, password: string, token: string, attribution?: AuthAttribution) {
  return apiRequest(
    () =>
      apiClient.POST('/signup', {
        body: { email, password, token, ...attributionBody(attribution) },
      }),
    i18n.global.t('api.signup'),
  )
}

export function recoverPassword(email: string, token: string) {
  return apiRequest(() => apiClient.POST('/recover-password', { body: { email, token } }), i18n.global.t('api.recover'))
}

// A challenge response must never be stored as an authenticated session.
function requireTokens(result: { token: string; refresh: string } | { status: string; challenge: string; expires_in: number }) {
  if ('token' in result) return result
  throw { status: 202, message: i18n.global.t('usersStudio.twoFactorRequired') }
}
